/**
 * A select's choices read by their keys, for a formula (F4 of
 * docs/plans/field-relations.md).
 *
 * A select stores the id of the choice picked, and a `date_add` that takes its
 * unit from a select wants the choice's key (days, weeks, months, years). So
 * every caller of `evaluateFormula` hands `FormulaContext.optionKey`, and this
 * is the one way the phone, the web and the server build it: every choice of
 * the schemas the caller holds, the row's own and those of the records a hop
 * lands on. Choice ids are unique, so one map answers both.
 *
 * It is built the first time it is asked, and it is asked only by a
 * `date_add` reading a select, so a recipe that reads none costs nothing.
 */
import { formulaChoicesRead, type FormulaSpec } from './formula';

/** A choice's key by its id, or null for a choice none of the schemas holds. */
export type OptionKeyReader = (optionId: string) => string | null;

/** One field as any side holds its choices: the wire schema's `options`. */
export type ChoiceField = {
  readonly options?: readonly { readonly id: string; readonly key?: string | null }[] | null;
};

/** What holds the fields: a record's schema, or the stored rows put in that shape. */
export type ChoiceSchema = { readonly fields: readonly ChoiceField[] };

/** Every choice of these schemas, id to key. A choice with no key is left out. */
export const optionKeysOf = (schemas: Iterable<ChoiceSchema | null | undefined>): OptionKeyReader => {
  let byId: Map<string, string> | null = null;
  return (optionId) => {
    if (byId === null) {
      byId = new Map();
      for (const schema of schemas) {
        for (const field of schema?.fields ?? []) {
          for (const option of field.options ?? []) {
            if (typeof option.key === 'string' && option.key !== '') byId.set(option.id.toLowerCase(), option.key);
          }
        }
      }
    }
    return byId.get(optionId.toLowerCase()) ?? null;
  };
};

/**
 * Every choice the row's formulas can reach: this record's, and those of every
 * record one of its references points at, read through `readSchema` (the
 * device's cached schemas). A schema the caller does not hold answers nothing,
 * and the `date_add` that wanted it answers nothing rather than a guess.
 */
export const optionKeysReaching = (
  schema: { readonly fields: readonly (ChoiceField & { readonly targetRecordId?: string | null })[] } | null,
  readSchema: (recordId: string) => ChoiceSchema | null,
): OptionKeyReader => {
  let reader: OptionKeyReader | null = null;
  return (optionId) => {
    if (reader === null) {
      const targets = new Set<string>();
      for (const field of schema?.fields ?? []) {
        if (typeof field.targetRecordId === 'string' && field.targetRecordId !== '') targets.add(field.targetRecordId);
      }
      const held: (ChoiceSchema | null)[] = [schema];
      for (const recordId of targets) {
        try {
          held.push(readSchema(recordId));
        } catch {
          // A store that cannot be read answers no choices, as an unheld schema does.
        }
      }
      reader = optionKeysOf(held);
    }
    return reader(optionId);
  };
};

/** Whether any of these recipes reads a select's choice by its key, so the choices are worth loading. */
export const readsChoiceKeys = (specs: Iterable<FormulaSpec | null | undefined>): boolean => {
  for (const spec of specs) if (spec != null && formulaChoicesRead(spec).length > 0) return true;
  return false;
};
